// Helpers compartidos para el manejo de módulos de licencias.
// Usados por IssueDialog, RequestRow y EditModulesButton.

import { ERP_MODULES_CONFIG, getModuleById } from "@/lib/modules";
import { ALWAYS_ENABLED_MODULES } from "@/convex/system/permissions/constants";
import { SYSTEM_MODULES } from "@/lib/licensing/tiers";
import type { Edition } from "@/convex/lib/config/types";

// ¿El grupo está disponible en esta edición? Los dominios joya declaran
// editions: ["saas","desktop"] → se excluyen de onprem/free_desktop (el build
// on-prem los prunea). Sin `editions` = disponible en todas.
export function isGroupInEdition(
  group: { editions?: readonly Edition[] },
  edition: Edition,
): boolean {
  return !group.editions || group.editions.includes(edition);
}

// Módulos grantables agrupados por dominio, FILTRADOS por edición (para el
// selector). Excluye always-enabled y los dominios no disponibles en esa edición.
export function getModuleGroups(edition: Edition) {
  return Object.values(ERP_MODULES_CONFIG.modules)
    .filter(
      (g) =>
        (!g.status || g.status === "active") && isGroupInEdition(g, edition),
    )
    .map((g) => ({
      name: g.name,
      modules: g.modules
        .filter((m) => !ALWAYS_ENABLED_MODULES.includes(m.id))
        .map((m) => ({ id: m.id, name: m.name })),
    }))
    .filter((g) => g.modules.length > 0);
}

// Set de ids (módulos + tablas) PERMITIDOS en la edición.
export function allowedIdsForEdition(edition: Edition): Set<string> {
  const set = new Set<string>();
  Object.values(ERP_MODULES_CONFIG.modules).forEach((g) => {
    if ((g.status && g.status !== "active") || !isGroupInEdition(g, edition))
      return;
    g.modules.forEach((m) => {
      set.add(m.id);
      m.tables.forEach((t) => set.add(t.tableName));
      m.readOnlyTables.forEach((t) => set.add(t.tableName));
    });
  });
  return set;
}

/**
 * Filtra `ids` dejando SOLO module ids válidos (los que existen en
 * ERP_MODULES_CONFIG). Las tablas huérfanas o strings sueltos se descartan.
 * Usado para limpiar `license.modules` que en DB podría contener mezclas
 * legacy de module ids + table names + SYSTEM_MODULES.
 */
export function moduleIdsOnly(ids: readonly string[]): string[] {
  return ids.filter((id) => getModuleById(id) !== undefined);
}

/**
 * Resuelve los permisos efectivos para un set de module ids grantables.
 * Devuelve dos Sets disjuntos:
 *   - `modules`: los module ids originales (para gating por módulo).
 *   - `tables`: las tablas propias + readOnly de cada módulo (para gating por tabla).
 *
 * NO persiste nada; es solo una vista computada en runtime. La idea es que
 * `license.modules` guarde SOLO module ids (lo que el super admin configuró)
 * y que el gating real se compute con esta función al consultar.
 *
 * Si `ids` está vacío, devuelve `null` para que el caller interprete como
 * "sin restricción" (convención ya existente: mods vacío = todos los módulos
 * de la edición).
 */
export function resolveEffectivePermissions(
  ids: readonly string[],
  _edition: Edition,
): { modules: Set<string>; tables: Set<string> } | null {
  if (ids.length === 0) return null;
  const modules = new Set<string>();
  const tables = new Set<string>();
  ids.forEach((id) => {
    const mod = getModuleById(id);
    if (!mod) return;
    modules.add(id);
    mod.tables.forEach((t) => tables.add(t.tableName));
    mod.readOnlyTables.forEach((t) => tables.add(t.tableName));
  });
  return { modules, tables };
}

/**
 * @deprecated Usar `resolveEffectivePermissions` en runtime. Esta función mezcla
 * módulos + tablas + SYSTEM_MODULES y los devuelve juntos, lo que provoca que
 * módulos deshabilitados por el super admin reaparezcan al guardar porque
 * otros módulos declaran sus tablas como `readOnlyTables` (ej: notifications
 * declara "reports" como readOnly). Se mantiene solo para compat con callers
 * que aún no migraron. NO USAR en código nuevo.
 */
export function expandModulesWithTables(
  ids: string[],
  edition: Edition,
): string[] {
  if (ids.length === 0) return [];
  const allowed = allowedIdsForEdition(edition);
  const base = [...SYSTEM_MODULES, ...ids].filter((id) => allowed.has(id));
  const set = new Set<string>(base);
  base.forEach((id) => {
    const mod = getModuleById(id);
    mod?.tables.forEach((t) => set.add(t.tableName));
    mod?.readOnlyTables.forEach((t) => set.add(t.tableName));
  });
  return Array.from(set);
}
